import {ThreadContext, ThreadProvider} from "../../context/threadContext";
import Body from "../common/Body";
import React, {useContext, useEffect} from "react";
import {boardUrl, TITLE} from "../../config/config";
import {Helmet} from "react-helmet";
import {generateHeadTags} from "react-seo-tools";
import Breadcrumb from "../common/Breadcrumb";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLock, faTags, faUser} from "@fortawesome/free-solid-svg-icons";
import {WidgetContext} from "../../context/widgetContext";
import HtmlParsedText from "../common/HtmlParsedText";
import ThreadPromoteNoti from "./section/ThreadPromoteNoti";
import {useTranslation} from "react-i18next";
import {HomeProvider} from "../../context/homeContext";
import ThreadActionButtons from "./section/ThreadActionButtons";
import TabbedContents from "../common/TabbedContents";
import MainPostContent from "./tabs/MainPostContent";
import Videos from "./tabs/Videos";
import Ratings from "./tabs/Ratings";
import FloatingCallButton from "./section/FloatingCallButton";

const SidebarContent = () => {
	const {
		tag_cloud,
		fetchWidgetHtml
	} = useContext(WidgetContext);

	useEffect(() => {
		fetchWidgetHtml('tag_cloud');
	}, [fetchWidgetHtml]);

	return (
		<>
			{tag_cloud && (
				<div className="mb-4">
					{tag_cloud && <HtmlParsedText text={tag_cloud}/>}
				</div>
			)}
		</>
	);
}

const HeroContent = () => {
	const {thread} = useContext(ThreadContext);

	if (thread) {
		let tags = Object.values(thread.tags);

		return (
			<div className="px-2 pt-2 md:px-4 md:pt-4">
				<section className="mb-4">
					<Breadcrumb items={thread.breadcrumbs}/>
				</section>

				<section className="flex items-center mb-2">
					<div>
						<h1 className="text-lg font-medium text-white">{thread.title}</h1>

						<div className="mt-2 text-sm text-url flex items-center flex-wrap whitespace-nowrap gap-x-2">
							<span>
								<FontAwesomeIcon icon={faUser} className="mr-1"/>
								<a href={thread.User ? thread.User.view_url : '#'}>{thread.username}</a>
							</span>

							<span>&#183;</span>

							<span>
								{thread.post_date}
							</span>
						</div>

						<ul className="mt-2">
							<FontAwesomeIcon icon={faTags} className="inline-block text-white mr-1"/>

							{tags.map((tag, index) => (
								<a key={index} href={boardUrl + '/tags/' + tag.tag_url}
								   className="inline-block bg-primary bg-opacity-65 text-xs text-white me-2 mb-1 px-2.5 py-0.5 rounded transition hover:bg-opacity-100">
									{tag.tag}
								</a>
							))}

							{thread.can_edit_tags && (
								<a href={`${thread.view_url}tags`}
								   className="inline-block text-xs text-white me-2 mb-1 px-2.5 py-0.5 rounded transition hover:underline">
									Chỉnh sửa tags
								</a>
							)}
						</ul>
					</div>
				</section>
			</div>
		);
	}
}

const HeroContentBottom = () => {
	const {thread} = useContext(ThreadContext);

	if (thread) {
		return (
			<div className="px-2 pt-2 md:px-4 md:pt-4">
				<section>
					<Breadcrumb items={thread.breadcrumbs}/>
				</section>
			</div>
		);
	}
}

const MainContent = () => {
	const {t} = useTranslation();

	const {
		thread, firstPost,
	} = useContext(ThreadContext);

	useEffect(() => {
		if (thread) {
			document.title = `${thread.title} | ${TITLE}`;
			document.querySelector('meta[name="description"]').setAttribute("content", thread.excerpt);
		}
	}, [thread]);

	const tabContents = [
		{
			label: 'BÀI VIẾT',
			content: <MainPostContent />
		},
	];

	if (thread && thread.has_videos) {
		tabContents.push({
			label: 'VIDEOS',
			content: <Videos />
		});
	}

	if (thread && thread.has_reviews) {
		tabContents.push({
			label: 'CHECKER ĐÁNH GIÁ',
			content: <Ratings />
		});
	}

	return (
		<div>
			{(thread && firstPost) && (
				<Helmet>
					{generateHeadTags({
						title: thread.title,
						description: thread.excerpt,
						openGraph: {
							type: 'article',
							title: thread.title,
							image: thread.thumbnail_url,
							'article:author': thread.username,
							'article:tag': thread.tags,
							'article:published_time': thread.post_date_meta_tag,
						},
					})}
				</Helmet>
			)}

			{(thread && thread.is_promote && thread.can_renew) && (
				<section className="mb-4">
					<ThreadPromoteNoti thread={thread} />
				</section>
			)}

			{(thread && thread.can_edit) && (
				<section className="mb-4 text-right">
					<ThreadActionButtons thread={thread} />
				</section>
			)}

			{(thread && !thread.discussion_open) && (
				<section className="mb-4">
					<div className="p-2 bg-secondary rounded text-white text-sm font-bold">
						<FontAwesomeIcon icon={faLock} className="mr-2" />
						Hiện không mở bình luận cho bài viết này
					</div>
				</section>
			)}

			<TabbedContents tabContents={tabContents} hasBorder={false} />

			{thread && (<FloatingCallButton thread={thread} />)}
		</div>
	);
}

const ThreadView = () => {
	return (
		<HomeProvider><ThreadProvider>
			<Body mainContent={<MainContent/>} heroContent={<HeroContent />} sidebarContent={<SidebarContent/>} heroContentBottom={<HeroContentBottom />} />
		</ThreadProvider></HomeProvider>
	);
}

export default ThreadView;